"use client";

import { useState } from "react";
import Image from "next/image";
import { Link } from "~/i18n/navigation";
import { ArrowRight } from "lucide-react";
import { useTranslations, useLocale } from "next-intl";

import { getPokemonImageSrc, getPokemonImageAlt } from "~/lib/pokemon";

interface QuestObj {
  image_url: string | null;
  last_seen: number | null;
  uuid: string;
  quest_target: number | null;
  title: string | null;
  translated_title?: string | null;
  condition_type: number | null;
  ar_task: number | null;
  no_ar_task: number | null;
  template?: string | null;
  task_slug?: string | null;
}

interface QuestsCardProps {
  questsData: Array<QuestObj> | undefined;
  title: string;
  i18n: {
    search: string;
    reward: string;
    quest: string;
    lastSeen: string;
    noResults: string;
  };
  pokemonName: string;
  formName?: string;
  costumeName?: string;
}

import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardAction,
} from "~/components/ui/card";
import { resolveQuestUrlSlug, isValidSlug } from "~/lib/quest-slugs";

const QuestsCard = ({ title, questsData, i18n, pokemonName, formName, costumeName }: QuestsCardProps) => {
  const locale = useLocale();
  const t = useTranslations("Pokemon");
  const [searchInput, setSearchInput] = useState("");
  const [showAll, setShowAll] = useState(false);

  const sortedData = [...(questsData ?? [])].sort((a, b) => (b.last_seen ?? 0) - (a.last_seen ?? 0));

  const searchResult = sortedData.filter((quest) => {
    return (
      quest.title?.toLowerCase().includes(searchInput.toLowerCase()) ?? false
    );
  });

  return (
    <Card className="col-span-12 overflow-hidden lg:col-span-7" id={title}>
      <CardHeader className="flex flex-row items-center justify-between pb-4">
        <CardTitle className="text-pory-blue-900 text-xl font-bold">
          {title}
        </CardTitle>
        <CardAction className="m-0">
          <input
            className="border-pory-pink-400 focus:ring-pory-pink-200 min-h-9 w-full max-w-[200px] rounded-md border-2 bg-white px-3 text-sm focus:ring-2 focus:outline-none"
            placeholder={i18n.search}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            type="search"
          />
        </CardAction>
      </CardHeader>
      <CardContent>
        {searchResult.length === 0 ? (
          <div className="flex h-24 items-center justify-center rounded-lg border border-neutral-200 bg-neutral-50 shadow-sm">
            <p className="text-neutral-500">{i18n.noResults}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {searchResult.map((quest, index) => {
              const hideClass = !showAll
                ? index === 3
                  ? "hidden sm:flex"
                  : index > 3
                    ? "hidden"
                    : ""
                : "";

              const questSlug = resolveQuestUrlSlug({
                taskSlug: quest.task_slug,
                translatedTitle: quest.translated_title,
                title: quest.title,
                target: quest.quest_target,
                locale,
              });

              const inner = (
                <>
                  <div className="relative flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-neutral-100 p-2">
                    {quest.image_url ? (
                      <Image
                        className="h-10 w-10 object-contain"
                        src={getPokemonImageSrc(quest.image_url)}
                        alt={getPokemonImageAlt(pokemonName, quest.image_url, false, formName, costumeName, locale)}
                        width={40}
                        height={40}
                      />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-neutral-200" />
                    )}
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                    <span className="text-pory-blue-900 line-clamp-2 font-bold leading-tight">
                      {quest.translated_title ??
                        quest.title
                          ?.split("_")
                          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                          .join(" ") ??
                        "-"}
                    </span>

                    <div className="flex flex-wrap gap-1.5">
                      {quest.ar_task === 1 && (
                        <span className="bg-pory-blue-100 text-pory-blue-900 rounded px-1.5 py-0.5 text-[10px] font-bold tracking-wider uppercase">
                          {t("withAR")}
                        </span>
                      )}
                      {quest.no_ar_task === 1 && (
                        <span className="bg-pory-pink-100 text-pory-pink-900 rounded px-1.5 py-0.5 text-[10px] font-bold tracking-wider uppercase">
                          {t("noAR")}
                        </span>
                      )}
                      {quest.template?.endsWith("_sb") && (
                        <span className="bg-amber-100 text-amber-900 rounded px-1.5 py-0.5 text-[10px] font-bold tracking-wider uppercase">
                          {t("background")}
                        </span>
                      )}
                    </div>

                    <span className="mt-auto pt-1 text-xs font-medium text-neutral-500">
                      {i18n.lastSeen}:{" "}
                      <span suppressHydrationWarning>
                        {quest.last_seen
                          ? new Date(quest.last_seen).toLocaleDateString()
                          : "-"}
                      </span>
                    </span>
                  </div>

                  {isValidSlug(questSlug) && (
                    <ArrowRight className="h-4 w-4 shrink-0 self-center text-neutral-400 group-hover:text-pory-blue-600 transition-colors" />
                  )}
                </>
              );

              const cardClass = `flex items-start gap-4 rounded-xl border border-neutral-200 bg-white p-4 shadow-sm transition-all hover:border-pory-pink-200 hover:shadow-md ${hideClass}`;

              if (isValidSlug(questSlug)) {
                return (
                  <Link
                    key={quest.uuid}
                    href={`/quests/${questSlug}`}
                    className={`${cardClass} group cursor-pointer`}
                  >
                    {inner}
                  </Link>
                );
              }

              return (
                <div key={quest.uuid} className={cardClass}>
                  {inner}
                </div>
              );
            })}
            {!showAll && searchResult.length > 3 && (
              <div
                className={`col-span-full mt-2 flex justify-center ${
                  searchResult.length === 4 ? "sm:hidden" : ""
                }`}
              >
                <button
                  onClick={() => setShowAll(true)}
                  className="rounded-full bg-pory-blue-50 px-4 py-2 text-sm font-bold text-pory-blue-900 transition-colors hover:bg-pory-blue-100"
                >
                  <span className="sm:hidden">{t("loadMore", { count: searchResult.length - 3 })}</span>
                  <span className="hidden sm:inline">{t("loadMore", { count: searchResult.length - 4 })}</span>
                </button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default QuestsCard;

